import React from "react"
import { useDispatch } from "react-redux"
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  Typography,
} from "@mui/material"
import { onAuthStateChanged, User } from "firebase/auth"
import gql from "graphql-tag"
import { useMutation, useQuery } from "@apollo/client"
import { Link } from "react-router-dom"
import {
  DEFAULT_REGISTRATION,
  LessonNotificationsRegistrationForm,
} from "./RegistrationForm"
import SignInModal from "../SignInModal"
import { sdk9Auth } from "../../../firebase"
import { CreateLessonNotificationRegistrationDocument } from "./RegistrationDialog.graphqlTypes"
import ButtonWithProgress from "../ButtonWithProgress"
import { useSelectorTyped } from "../../../store/configureStore"
import { getAvailabilityForLocationPayload } from "../../../actions/searchActions"
import { setSnackbar } from "../../../actions/globalUiActions"
import { GetLessonNotificationRegistrationsDocument } from "./UserLessonNotifications.graphqlTypes"

gql`
  fragment LessonNotificationRegistrationRangeView on LessonNotificationRegistrationRange {
    day
    startTime
    endTime
  }

  fragment LessonNotificationRegistrationView on LessonNotificationRegistration {
    id
    notificationInterval
    ranges {
      ...LessonNotificationRegistrationRangeView
    }
    coords {
      lat
      lng
    }
    address
    lessonType
  }

  mutation CreateLessonNotificationRegistration(
    $studentId: ID!
    $input: LessonNotificationRegistrationInput!
    $payload: JSONObject!
  ) {
    registration: createLessonNotificationRegistration(
      studentId: $studentId
      input: $input
      payload: $payload
    ) {
      ...LessonNotificationRegistrationView
    }
  }
`

interface Props {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

export function LessonNotificationsRegistrationDialog({
  isOpen,
  setIsOpen,
}: Props) {
  const dispatch = useDispatch()
  const { search, location } = useSelectorTyped((state) => ({
    search: state.search,
    location: state.location,
  }))
  const [registration, setRegistration] = React.useState(DEFAULT_REGISTRATION)
  const [user, setUser] = React.useState<User | null>(null)
  React.useEffect(() => {
    const unsub = onAuthStateChanged(sdk9Auth, setUser)

    return () => unsub()
  }, [])

  const [createMutation, createResult] = useMutation(
    CreateLessonNotificationRegistrationDocument,
    {
      onCompleted: () => {
        setIsOpen(false)
        setRegistration(DEFAULT_REGISTRATION)
        dispatch(
          setSnackbar(
            "Your notifications have been saved. Visit your profile to make changes.",
            "success",
          ),
        )
      },
    },
  )

  const query = useQuery(GetLessonNotificationRegistrationsDocument, {
    fetchPolicy: "network-only",
    skip: !isOpen || !user,
    variables: {
      query: { studentId: user?.uid },
      pageQuery: { first: 100 }, // very unlikely to have more than 100 records
    },
  })

  const registrations = query.data?.registrations.edges.map((edge) => edge.node)

  const handleSubmit = () => {
    if (!user?.uid) {
      return
    }

    const { searchAddress, stateConfig } = location
    const {
      startDate,
      endDate,
      outOfServiceArea,
      includePrivate,
      selectedLessonType,
    } = search

    if (outOfServiceArea || !searchAddress) {
      return
    }

    const payload = getAvailabilityForLocationPayload({
      location: {
        address: searchAddress,
        distanceMiles: 0,
        meetingPointLabel: "Home Pickup",
        meetingPointId: "home",
      },
      instructorId: undefined,
      durationMinutes: selectedLessonType.durationMinutes,
      startDate,
      endDate,
      includePrivate,
      selectedLessonType,
      isAdmin: false,
      lastInstuctorId: null,
      searchState: stateConfig?.shortName,
    })

    createMutation({
      variables: {
        studentId: user.uid,
        input: registration,
        payload,
      },
    })
  }

  if (!user) {
    return (
      <SignInModal
        message="Please sign in or sign up to register for lesson notifications."
        open={isOpen}
        closeCallback={() => setIsOpen(false)}
        signInCallback={() => {}}
        showEmailChangeAction={false}
        displaySignUpBtn
      />
    )
  }

  return (
    <Dialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      fullWidth
      data-cy="lesson-notifications-dialog"
    >
      <DialogTitle>Register for Notifications</DialogTitle>
      {createResult.error && (
        <Alert severity="error">
          {createResult.error.message ??
            "There was a problem saving your registration. Please try again."}
        </Alert>
      )}
      {search.outOfServiceArea && (
        <Alert severity="warning">
          The address you are searching for is in an out of service area.
        </Alert>
      )}
      <LessonNotificationsRegistrationForm
        title={
          <>
            <Typography>
              You have{" "}
              <Chip label={registrations?.length ?? 0} color="secondary" />{" "}
              existing registration
              {(registrations?.length ?? 0) > 1 || !registrations?.length
                ? "s"
                : ""}
              .
            </Typography>
            <Typography mt={1}>
              Go to your <Link to="/profile/lesson-notifications">profile</Link>{" "}
              to manage them.
            </Typography>
            <Typography mt={1} mb={1}>
              We will notify you when new lesson times are added that match the
              availability you enter below.
            </Typography>
          </>
        }
        withDialog
        disabled={search.outOfServiceArea}
        registration={registration}
        setRegistration={setRegistration}
        renderActions={() => (
          <>
            <ButtonWithProgress
              clickedCallback={handleSubmit}
              isLoading={createResult.loading}
              variant="contained"
              color="primary"
            >
              Register
            </ButtonWithProgress>
            <Button
              onClick={() => setIsOpen(false)}
              disabled={createResult.loading}
              variant="outlined"
              color="primary"
            >
              Close
            </Button>
          </>
        )}
      />
    </Dialog>
  )
}
